import {
  entityEdgesWithoutSubset,
  moveToFulfilled,
  moveToPending,
  moveToRejected,
  sortEntityEdges,
  update,
} from '@ms/yammer-data-support';
import {
  CampaignFeedType,
  GroupFeedType,
  HomeFeedType,
  NetworkQuestionFeedType,
  TopicFeedType,
} from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { DeleteMessageFulfilledAction } from '../../domains/deleteMessage/actions.types';
import { DeleteThreadFulfilledAction } from '../../domains/deleteThread/actions.types';
import {
  FeedLoadAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import {
  FeedMarkAllGroupMessagesAsSeenFulfilledAction,
  RemoveThreadByAdminActionAction,
  UpdateThreadSortKeyAction,
} from '../../domains/feeds/actions.types';
import {
  attachableLinkFeedId,
  getId as getFeedId,
  inboxAllFeedId,
  inboxDirectFeedId,
  inboxUnreadFeedId,
} from '../../domains/feeds/getId';
import { ThreadHideFromCampaignFeedFulfilledAction } from '../../domains/hiddenThreads/campaignActions.types';
import { ThreadMarkAllInboxReadAction } from '../../domains/inboxMarkAllAsRead/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishTeamsMeetingMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserMomentMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { ThreadMarkAsSeenFulfilledAction } from '../../domains/threadMarkAsGroupSeenOnServer/actions.types';
import {
  ThreadMarkAsInboxReadAction,
  ThreadMarkAsInboxReadFulfilledAction,
  ThreadMarkAsInboxUnreadAction,
  ThreadMarkAsInboxUnreadFulfilledAction,
  ThreadMarkAsInboxUnreadRealtimeAction,
} from '../../domains/threadMarkAsRead/actions.types';
import { MessageDeletionOutcome } from '../../normalization/types';
import { Thread, ThreadEdge } from '../types';

import { Feed, FeedType, Feeds } from './types';

export type FeedAction =
  | FeedLoadAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishTeamsMeetingMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserMomentMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | ThreadMarkAsInboxReadFulfilledAction
  | ThreadMarkAsInboxReadAction
  | ThreadMarkAsInboxUnreadAction
  | ThreadMarkAsSeenFulfilledAction
  | DeleteMessageFulfilledAction
  | DeleteThreadFulfilledAction
  | ThreadMarkAllInboxReadAction
  | FeedMarkAllGroupMessagesAsSeenFulfilledAction
  | PublishEditMessageFulfilledAction
  | RemoveThreadByAdminActionAction
  | UpdateThreadSortKeyAction
  | PublishNetworkQuestionMessageFulfilledAction
  | ThreadHideFromCampaignFeedFulfilledAction;

const initialState: Feeds = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const hasThreadEdgeWithId = (threadEdges: ThreadEdge[], id: Thread['id']) =>
  !!threadEdges.find((edge) => edge.nodeId === id);

const shouldShowThreadInFeed = (feed: Feed, threadSortKey: string) => {
  const oldestStoredThread = feed.threadEdges[feed.threadEdges.length - 1];
  const oldestStoredThreadSortKey = oldestStoredThread ? oldestStoredThread.sortKey : undefined;

  return !feed.hasOlderThreads || !oldestStoredThreadSortKey || threadSortKey > oldestStoredThreadSortKey;
};

const appendOlderThreadsToFeed = (state: Feeds, action: FeedLoadOlderThreadsFulfilledAction) => {
  const id = action.payload.id;
  const payloadFeed = action.payload.entities.feeds[id];
  const feed = state.fulfilled.byId[id];
  const newUserSuggestionCardEdges = payloadFeed?.userSuggestionCardEdges || [];
  const existingUserSuggestionCardEdges = feed?.userSuggestionCardEdges || [];

  if (!feed) {
    return state;
  }

  const updatedFeed = {
    threadEdges: feed.threadEdges.concat(payloadFeed.threadEdges),
    userSuggestionCardEdges: [...existingUserSuggestionCardEdges, ...newUserSuggestionCardEdges],
    hasOlderThreads: payloadFeed.hasOlderThreads,
  };

  return update(state, id, updatedFeed);
};

const prependNewerThreadEdgesToFeed = (state: Feeds, threadEdges: ThreadEdge[], feedId: Feed['id']) => {
  const feed = state.fulfilled.byId[feedId];

  if (!feed) {
    return state;
  }

  const feedEdgesWithoutNewThreads = feed.threadEdges.filter((edge) => !hasThreadEdgeWithId(threadEdges, edge.nodeId));

  const updatedFeed = {
    threadEdges: [...threadEdges, ...feedEdgesWithoutNewThreads],
  };

  return update(state, feedId, updatedFeed);
};

const shouldReplaceEdges = (feed: Feed) => feed.hasOlderThreads && feed.type === FeedType.Inbox;

const addLoadedNewerThreadsToFeed = (state: Feeds, action: FeedLoadNewerThreadsFulfilledAction) => {
  const id = action.payload.id;
  const feed = action.payload.entities.feeds[id];

  if (!feed) {
    return state;
  }

  if (shouldReplaceEdges(feed)) {
    return update(state, id, feed);
  }

  return prependNewerThreadEdgesToFeed(state, feed.threadEdges, id);
};

const prependNewlyPublishedThreadToFeed = (state: Feeds, threadId: Thread['id'], feedId: Feed['id']) =>
  // We're not able to populate cursor or sortKey here, this may be an edge-case issue for pagination
  prependNewerThreadEdgesToFeed(state, [{ nodeId: threadId, isThreadStarterPostedByCurrentUser: true }], feedId);

const moveNewlyRepliedThreadToTopOfFeed = (state: Feeds, threadId: Thread['id'], feedId?: Feed['id']) => {
  if (!feedId) {
    return state;
  }
  const feed = state.fulfilled.byId[feedId];
  if (!feed) {
    return state;
  }

  const threadIndex = feed.threadEdges.findIndex((edge) => edge.nodeId === threadId);
  if (threadIndex === -1) {
    return state;
  }

  const updatedThread: ThreadEdge = { ...feed.threadEdges[threadIndex], wasLastReplyPostedByCurrentUser: true };
  const otherThreadEdges = [...feed.threadEdges.filter((edge) => edge.nodeId !== threadId)];

  return {
    ...state,
    fulfilled: {
      byId: {
        ...state.fulfilled.byId,
        [feedId]: {
          ...feed,
          threadEdges: [updatedThread, ...otherThreadEdges],
        },
      },
    },
  };
};

const addThreadEdgeToFeedInPosition = (state: Feeds, feed: Feed, threadEdge: ThreadEdge) => {
  const updatedThreadEdges = sortEntityEdges<Thread>([...feed.threadEdges, threadEdge]);

  const updatedFeed = {
    ...feed,
    threadEdges: updatedThreadEdges,
  };

  return update(state, inboxUnreadFeedId, updatedFeed);
};

const threadEdgeIsAlreadyStored = (storedThreadEdges: ThreadEdge[], newThreadEdge: ThreadEdge) =>
  storedThreadEdges.find((edge) => edge.nodeId === newThreadEdge.nodeId) != null;

const updateUnreadFeedAfterMarkAsInboxUnread = (
  state: Feeds,
  action: ThreadMarkAsInboxUnreadFulfilledAction | ThreadMarkAsInboxUnreadRealtimeAction
) => {
  const feeds = state.fulfilled.byId;
  const feed = feeds[inboxUnreadFeedId];
  if (!feed) {
    return state;
  }

  const threadEdge = action.payload.threadEdge;

  // We need the edge's sortKey to place the threadEdge in its correct place in the feed.
  if (!threadEdge.sortKey) {
    return state;
  }

  const isNotAlreadyInFeed = !threadEdgeIsAlreadyStored(feed.threadEdges, threadEdge);

  return shouldShowThreadInFeed(feed, threadEdge.sortKey) && isNotAlreadyInFeed
    ? addThreadEdgeToFeedInPosition(state, feed, threadEdge)
    : state;
};

const removeThreadsFromFeed = (state: Feeds, threadEdges: ThreadEdge[], feedId: Feed['id']) => {
  const feed = state.fulfilled.byId[feedId];
  if (!feed || feed.type === FeedType.HomeMoments || feed.type === FeedType.UserMoments) {
    return state;
  }

  const otherThreadEdges = entityEdgesWithoutSubset<Thread>(feed.threadEdges, threadEdges);

  if (otherThreadEdges.length === feed.threadEdges.length) {
    return state;
  }

  const updatedFeed = {
    ...feed,
    threadEdges: otherThreadEdges,
  };

  return update(state, feedId, updatedFeed);
};

const removeAllThreadsFromFeed = (state: Feeds, feedId: Feed['id']) => {
  const feed = state.fulfilled.byId[feedId];
  if (!feed) {
    return state;
  }

  const updatedFeed = {
    ...feed,
    threadEdges: [],
    hasOlderThreads: false,
  };

  return update(state, feedId, updatedFeed);
};

const removeMessageFromAllFeeds = (
  state: Feeds,
  { threadId, deletionOutcome }: DeleteMessageFulfilledAction['payload']
) => {
  if (deletionOutcome !== MessageDeletionOutcome.THREAD_REMOVED) {
    return state;
  }

  return removeThreadFromAllFeeds(state, threadId);
};

const removeThreadFromAllFeeds = (state: Feeds, threadId: Thread['id']) =>
  Object.keys(state.fulfilled.byId).reduce(
    (updatedState, feedId) => removeThreadsFromFeed(updatedState, [{ nodeId: threadId }], feedId),
    state
  );
const updateUnseenFeedsOnMarkSeen = (state: Feeds, action: ThreadMarkAsSeenFulfilledAction) => {
  const feedIds = Object.keys(action.payload.threadEdgesToRemoveByFeedId);

  return feedIds.reduce((newState, feedId) => {
    const threadEdgesToRemove = action.payload.threadEdgesToRemoveByFeedId[feedId];

    return removeThreadsFromFeed(newState, threadEdgesToRemove, feedId);
  }, state);
};

const prependNewlyPublishedThreadToFeeds = (
  state: Feeds,
  { threadId, campaignId, groupId, isQuestion, userId, topicId, isDraft }: PublishGroupMessageFulfilledAction['payload']
) => {
  if (isDraft) {
    return state;
  }

  const allFeedId = getFeedId({ type: FeedType.Group, subtype: GroupFeedType.ALL, entityId: groupId });
  const stateWithUpdatedGroupAllFeed = prependNewlyPublishedThreadToFeed(state, threadId, allFeedId);

  const newFeedId = getFeedId({ type: FeedType.Group, subtype: GroupFeedType.UNSEEN, entityId: groupId });
  const stateWithUpdatedGroupNewFeed = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedGroupAllFeed,
    threadId,
    newFeedId
  );

  const questionFeedId = getFeedId({ type: FeedType.Group, subtype: GroupFeedType.QUESTIONS, entityId: groupId });
  const stateWithUpdatedQuestionFeed = isQuestion
    ? prependNewlyPublishedThreadToFeed(stateWithUpdatedGroupNewFeed, threadId, questionFeedId)
    : stateWithUpdatedGroupNewFeed;

  const unansweredFeedId = getFeedId({
    type: FeedType.Group,
    subtype: GroupFeedType.UNANSWERED_QUESTIONS,
    entityId: groupId,
  });
  const stateWithUpdatedUnansweredFeed = isQuestion
    ? prependNewlyPublishedThreadToFeed(stateWithUpdatedQuestionFeed, threadId, unansweredFeedId)
    : stateWithUpdatedGroupNewFeed;

  const homeAllFeedId = getFeedId({
    type: FeedType.Home,
    subtype: HomeFeedType.ALL_PUBLIC_GROUPS,
  });
  const stateWithUpdatedGroupAndHomeAllFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUnansweredFeed,
    threadId,
    homeAllFeedId
  );

  const homeDiscoveryFeedId = getFeedId({
    type: FeedType.Home,
    subtype: HomeFeedType.DISCOVERY,
  });
  const stateWithUpdatedGroupAndHomeFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedGroupAndHomeAllFeeds,
    threadId,
    homeDiscoveryFeedId
  );

  const userProfileFeedId = getFeedId({
    type: FeedType.User,
    entityId: userId,
  });
  const stateWithUpdatedGroupAndUserAndHomeFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedGroupAndHomeFeeds,
    threadId,
    userProfileFeedId
  );

  const feedWithUserElseGroupAndHome = userId
    ? stateWithUpdatedGroupAndUserAndHomeFeeds
    : stateWithUpdatedGroupAndHomeFeeds;

  const stateWithUpdatedGroupHomeInboxFeeds = prependNewlyPublishedThreadToFeed(
    feedWithUserElseGroupAndHome,
    threadId,
    inboxAllFeedId
  );

  // Update Attachable Link
  const stateWithUpdatedAttachableLink = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedGroupHomeInboxFeeds,
    threadId,
    attachableLinkFeedId
  );

  const allCampaignFeedId = campaignId
    ? getFeedId({ type: FeedType.Campaign, subtype: CampaignFeedType.ALL, entityId: campaignId })
    : undefined;

  const stateWithUpdatedCampaignFeed = allCampaignFeedId
    ? prependNewlyPublishedThreadToFeed(stateWithUpdatedAttachableLink, threadId, allCampaignFeedId)
    : stateWithUpdatedAttachableLink;

  // Update TOPIC ALL
  if (!topicId) {
    return stateWithUpdatedCampaignFeed;
  }

  const topicAllFeedId = getFeedId({
    type: FeedType.Topic,
    subtype: TopicFeedType.ALL,
    entityId: topicId,
  });

  const stateWithUpdatedTopicAllFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedCampaignFeed,
    threadId,
    topicAllFeedId
  );

  // Update TOPIC QUESTIONS
  const topicQuestionsFeedId = getFeedId({
    type: FeedType.Topic,
    subtype: TopicFeedType.QUESTIONS,
    entityId: topicId,
  });

  const stateWithUpdatedTopicQuestions = isQuestion
    ? prependNewlyPublishedThreadToFeed(stateWithUpdatedTopicAllFeeds, threadId, topicQuestionsFeedId)
    : stateWithUpdatedTopicAllFeeds;

  return stateWithUpdatedTopicQuestions;
};

const prependNewlyPublishedThreadToUserFeeds = (
  state: Feeds,
  { threadId, userId, campaignId, isDraft }: PublishUserMessageFulfilledAction['payload']
) => {
  if (isDraft) {
    return state;
  }

  const userFeedId = getFeedId({
    type: FeedType.User,
    entityId: userId,
  });
  const stateWithUpdatedUserFeed = prependNewlyPublishedThreadToFeed(state, threadId, userFeedId);

  const userWallFeedId = getFeedId({
    type: FeedType.UserWall,
    entityId: userId,
  });
  const stateWithUpdatedUserWallFeed = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUserFeed,
    threadId,
    userWallFeedId
  );

  const homeAllFeedId = getFeedId({
    type: FeedType.Home,
    subtype: HomeFeedType.ALL_PUBLIC_GROUPS,
  });
  const stateWithUpdatedUserAndHomeAllFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUserWallFeed,
    threadId,
    homeAllFeedId
  );

  const homeDiscoveryFeedId = getFeedId({
    type: FeedType.Home,
    subtype: HomeFeedType.DISCOVERY,
  });
  const stateWithUpdatedUserAndHomeFeeds = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUserAndHomeAllFeeds,
    threadId,
    homeDiscoveryFeedId
  );
  const storylineDiscoveryFeedId = getFeedId({
    type: FeedType.StorylineDiscovery,
  });

  const stateWithDiscoveryFeed = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUserAndHomeFeeds,
    threadId,
    storylineDiscoveryFeedId
  );

  const storylineFollowedFeedId = getFeedId({
    type: FeedType.StorylineFollowed,
  });

  const stateWithStorylineFollowedFeed = prependNewlyPublishedThreadToFeed(
    stateWithDiscoveryFeed,
    threadId,
    storylineFollowedFeedId
  );

  const storylineAllFeedId = getFeedId({
    type: FeedType.StorylineAll,
  });

  const stateWithAllFeed = prependNewlyPublishedThreadToFeed(
    stateWithStorylineFollowedFeed,
    threadId,
    storylineAllFeedId
  );

  const allCampaignFeedId = campaignId
    ? getFeedId({
        type: FeedType.Campaign,
        subtype: CampaignFeedType.ALL,
        entityId: campaignId,
      })
    : undefined;

  const stateWithUpdatedUserAndHomeAndCampaignFeed = allCampaignFeedId
    ? prependNewlyPublishedThreadToFeed(stateWithAllFeed, threadId, allCampaignFeedId)
    : stateWithAllFeed;

  return stateWithUpdatedUserAndHomeAndCampaignFeed;
};

const prependNewlyPublishedThreadToUserMomentFeeds = (
  state: Feeds,
  { threadId, userId }: PublishUserMomentMessageFulfilledAction['payload']
) => {
  const feedId = getFeedId({
    type: FeedType.UserMoments,
    entityId: userId,
  });
  const stateWithUpdatedUserFeed = prependNewlyPublishedThreadToFeed(state, threadId, feedId);

  const userMomentFeedId = getFeedId({
    type: FeedType.UserMoments,
    entityId: userId,
  });
  const stateWithUpdatedUserMomentFeed = prependNewlyPublishedThreadToFeed(
    stateWithUpdatedUserFeed,
    threadId,
    userMomentFeedId
  );

  return stateWithUpdatedUserMomentFeed;
};

const prependNewlyPublishedThreadToNetworkQuestionFeeds = (
  state: Feeds,
  { threadId }: PublishNetworkQuestionMessageFulfilledAction['payload']
) => {
  const networkQuestionFeedId = getFeedId({
    type: FeedType.NetworkQuestion,
    subtype: NetworkQuestionFeedType.ALL,
  });

  const stateWithUpdatedNetworkQuestionFeed = prependNewlyPublishedThreadToFeed(state, threadId, networkQuestionFeedId);

  return stateWithUpdatedNetworkQuestionFeed;
};

const feedWithoutUnseenThreads = (state: Feeds, feedId: Feed['id']) => {
  const feedWithoutEdges = removeAllThreadsFromFeed(state, feedId);

  return update(feedWithoutEdges, feedId, { hasOlderThreads: false });
};

const removeThreadFromInboxDraftFeed = (
  state: Feeds,
  { threadId, wasPublishedAsDraft, isScheduledPost }: PublishEditMessageFulfilledAction['payload']
) => {
  if (wasPublishedAsDraft) {
    return state;
  }
  const type = isScheduledPost ? FeedType.InboxScheduled : FeedType.InboxDraft;

  const feedId = getFeedId({ type });

  return removeThreadsFromFeed(state, [{ nodeId: threadId }], feedId);
};

const updateThreadSortKey = (state: Feeds, { feedId, threadId, sortKey }: UpdateThreadSortKeyAction['payload']) => {
  const feed = state.fulfilled.byId[feedId];
  if (!feed) {
    return state;
  }

  const { threadEdges } = feed;
  const threadIndex = threadEdges.findIndex((thread) => thread.nodeId === threadId);
  const updatedThread: ThreadEdge =
    threadIndex >= 0 ? { ...threadEdges[threadIndex], sortKey } : { nodeId: threadId, sortKey };

  const threadEdgesWithoutThread = threadEdges.filter((edge) => edge.nodeId !== threadId);
  const updatedThreadEdges = [...threadEdgesWithoutThread, updatedThread];

  const updatedFeed = {
    ...feed,
    threadEdges: sortEntityEdges<Thread>(updatedThreadEdges),
  };

  return update(state, feedId, updatedFeed);
};

const reducer: Reducer<Feeds, FeedAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_PENDING':
      return moveToPending(state, [action.payload.id]);
    case 'FEED.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.id], action.payload.error);
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.feeds);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return appendOlderThreadsToFeed(state, action);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return addLoadedNewerThreadsToFeed(state, action);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED': {
      const { threadId, feedId } = action.payload;
      const stateWithUpdatedEventFeed = prependNewlyPublishedThreadToFeed(state, threadId, feedId);

      return prependNewlyPublishedThreadToFeed(stateWithUpdatedEventFeed, threadId, inboxAllFeedId);
    }
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_MOMENT_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToUserMomentFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToUserFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToUserFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToUserFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED': {
      const { threadId } = action.payload;
      const stateWithUpdatedGroupFeed = prependNewlyPublishedThreadToFeed(state, threadId, inboxDirectFeedId);

      return prependNewlyPublishedThreadToFeed(stateWithUpdatedGroupFeed, threadId, inboxAllFeedId);
    }
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED': {
      const { threadId, feedId } = action.payload;
      const stateWithUpdatedGroupFeed = moveNewlyRepliedThreadToTopOfFeed(state, threadId, feedId);

      return moveNewlyRepliedThreadToTopOfFeed(stateWithUpdatedGroupFeed, threadId, inboxAllFeedId);
    }
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToFeeds(state, action.payload);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return removeThreadFromInboxDraftFeed(state, action.payload);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED': {
      const { threadId, feedId } = action.payload;

      return prependNewlyPublishedThreadToFeed(state, threadId, feedId);
    }
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return prependNewlyPublishedThreadToNetworkQuestionFeeds(state, action.payload);
    case 'THREAD.DELETE_FULFILLED':
      return removeThreadFromAllFeeds(state, action.payload.threadId);
    case 'THREAD.MARK_AS_INBOX_READ_FULFILLED':
      return removeThreadsFromFeed(state, [action.payload.threadEdge], inboxUnreadFeedId);
    case 'THREAD.MARK_AS_INBOX_READ_REALTIME':
      return removeThreadsFromFeed(state, [action.payload.threadEdge], inboxUnreadFeedId);
    case 'THREAD.MARK_AS_INBOX_UNREAD_FULFILLED':
      return updateUnreadFeedAfterMarkAsInboxUnread(state, action);
    case 'THREAD.MARK_ALL_AS_INBOX_READ_REALTIME':
      return removeAllThreadsFromFeed(state, inboxUnreadFeedId);
    case 'THREAD.MARK_AS_INBOX_UNREAD_REALTIME':
      return updateUnreadFeedAfterMarkAsInboxUnread(state, action);
    case 'MESSAGE.DELETE_FULFILLED':
      return removeMessageFromAllFeeds(state, action.payload);
    case 'THREAD.MARK_AS_GROUP_SEEN_FULFILLED':
      return updateUnseenFeedsOnMarkSeen(state, action);
    case 'THREAD.MARK_ALL_INBOX_READ_FULFILLED':
      return removeAllThreadsFromFeed(state, inboxUnreadFeedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_FULFILLED':
      return feedWithoutUnseenThreads(state, action.payload.feedId);
    case 'FEED.REMOVE_THREAD_BY_ADMIN_ACTION':
      return removeThreadsFromFeed(state, [{ nodeId: action.payload.threadId }], action.payload.feedId);
    case 'FEED.UPDATE_THREAD_SORT_KEY':
      return updateThreadSortKey(state, action.payload);

    default:
      return state;
  }
};

export default reducer;
